import {
  CHIEF_SPAWN_KEY,
  groupBotsForSidebar,
  isRunWaitingOnUser,
  isRunWorking,
} from "@rakazo/core";

type SidebarChat = {
  id: string;
  name: string;
  preview?: string | null;
  pinned: boolean;
  sectionId: string | null;
  unread?: boolean;
  superBot?: boolean;
  spawnKey?: string | null;
};

/** The Space's Super bot and the owner's Chief sit on their own at the top of the list. */
export function leadsTheSpace(chat: { superBot?: boolean; spawnKey?: string | null }): boolean {
  return Boolean(chat.superBot) || chat.spawnKey === CHIEF_SPAWN_KEY;
}

/** The roster's filter chips. */
export const ROSTER_FILTERS = ["all", "needs-you", "working", "unread"] as const;
export type RosterFilter = (typeof ROSTER_FILTERS)[number];

/**
 * Keeps the chats a roster filter shows. Needs you also keeps every chat with something waiting
 * in the inbox (`waitingIds`: an ask or approval card, or a fact proposal), so the filter lists
 * what the inbox does even when the bot has moved on to another run. Groups have no run status.
 */
export function filterSpaceChats<
  TBot extends SidebarChat & { status: string },
  TGroup extends SidebarChat,
  TSpace extends { bots: readonly TBot[]; groups: readonly TGroup[] },
>(space: TSpace, filter: RosterFilter, waitingIds: ReadonlySet<string> = new Set()): TSpace {
  if (filter === "all") return space;
  const keepBot = (bot: TBot) =>
    filter === "needs-you"
      ? isRunWaitingOnUser(bot.status) || waitingIds.has(bot.id)
      : filter === "working"
        ? isRunWorking(bot.status)
        : Boolean(bot.unread);
  const keepGroup = (group: TGroup) =>
    filter === "unread"
      ? Boolean(group.unread)
      : filter === "needs-you" && waitingIds.has(group.id);
  return {
    ...space,
    bots: space.bots.filter(keepBot),
    groups: space.groups.filter(keepGroup),
  };
}

/** Bots in a Space waiting on the user, from the roster's live statuses. */
export function countNeedsYou(bots: readonly { status: string }[]): number {
  return bots.filter((bot) => isRunWaitingOnUser(bot.status)).length;
}

type SidebarSpace<TBot extends SidebarChat, TGroup extends SidebarChat> = {
  id: string;
  name: string;
  canRename?: boolean;
  canDelete?: boolean;
  bots: readonly TBot[];
  groups: readonly TGroup[];
  botSections: readonly { id: string; name: string }[];
};

function spaceHeaderMenu(space: { canRename?: boolean; canDelete?: boolean }, hostsMenu: boolean) {
  return {
    canRenameSpace: hostsMenu && space.canRename === true,
    canDeleteSpace: hostsMenu && space.canDelete === true,
    // Every member can open a space's settings, so the menu does not depend on rename or delete.
    canOpenSpaceSettings: hostsMenu,
  };
}

export async function commitSpaceRename(input: {
  rename: () => Promise<void>;
  apply: () => void;
  refresh: () => Promise<void>;
}): Promise<void> {
  await input.rename();
  input.apply();
  await input.refresh().catch(() => undefined);
}

export function sidebarGroupsForSpaces<TBot extends SidebarChat, TGroup extends SidebarChat>(
  spaces: readonly SidebarSpace<TBot, TGroup>[],
  query: string,
  labels?: { pinned: string; unassigned: string; groups?: string },
  /** A roster filter is on: like a search, it hides what it leaves empty. */
  filtered = false,
) {
  const needle = query.toLowerCase();
  const hiding = Boolean(needle) || filtered;
  const showSpaceNames = spaces.length > 1;
  return spaces.flatMap((space) => {
    const visibleBots = space.bots.filter((bot) =>
      `${bot.name} ${bot.preview ?? ""}`.toLowerCase().includes(needle),
    );
    const visibleGroups = space.groups.filter((group) =>
      `${group.name} ${group.preview ?? ""}`.toLowerCase().includes(needle),
    );
    const sectionIds = new Set(space.botSections.map((section) => section.id));
    // A group chat nobody pinned or filed in a section gets its own Groups list rather than
    // sitting among the unassigned bots.
    const looseGroup = (group: TGroup) =>
      !group.pinned && !(group.sectionId && sectionIds.has(group.sectionId));
    const asItem = <T extends { kind: "bot" | "group"; chat: TBot | TGroup }>(item: T) => ({
      ...item,
      id: item.chat.id,
      pinned: item.chat.pinned,
      sectionId: item.chat.sectionId,
      lead: item.kind === "bot" && leadsTheSpace(item.chat),
    });
    const grouped = groupBotsForSidebar(
      [
        ...visibleBots.map((chat) => ({ kind: "bot" as const, chat })),
        ...visibleGroups
          .filter((group) => !looseGroup(group))
          .map((chat) => ({ kind: "group" as const, chat })),
      ].map(asItem),
      space.botSections,
      { labels },
    );
    const loose = visibleGroups
      .filter(looseGroup)
      .map((chat) => asItem({ kind: "group" as const, chat }));
    if (loose.length > 0) {
      grouped.push({ key: "groups", title: labels?.groups ?? "Groups", bots: loose });
    }
    // Keep destinations visible after their last bot is dragged out. Search
    // still hides empty lists, and spaces without sections keep their old layout.
    if (!hiding && space.botSections.length > 0 && grouped.length > 0) {
      for (const section of space.botSections) {
        const key = `section:${section.id}`;
        if (!grouped.some((group) => group.key === key)) {
          grouped.push({ key, title: section.name, bots: [] });
        }
      }
      if (!grouped.some((group) => group.key === "unassigned")) {
        grouped.push({ key: "unassigned", title: labels?.unassigned ?? "Unassigned", bots: [] });
      }
      const order = [
        "lead",
        "pinned",
        ...space.botSections.map((section) => `section:${section.id}`),
        "unassigned",
        "groups",
      ];
      grouped.sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
    }
    const sections = grouped.map((group, index) => ({
      ...group,
      sectionId: group.key.startsWith("section:") ? group.key.slice("section:".length) : null,
      key: showSpaceNames ? `space:${space.id}:${group.key}` : group.key,
      // A header holding the space menu is never blank, even for the only space.
      title: showSpaceNames ? space.name : index === 0 && !group.title ? space.name : group.title,
      subtitle: showSpaceNames && group.title ? group.title : undefined,
      showLock: showSpaceNames,
      emptySpaceId: undefined as string | undefined,
      spaceId: space.id,
      spaceName: space.name,
      ...spaceHeaderMenu(space, index === 0),
    }));
    if (sections.length > 0) return sections;
    // Keep empty spaces selectable; chat clicks are the only switch control.
    if (filtered || (needle && (space.bots.length > 0 || space.groups.length > 0))) return [];
    // Saved sections stay listed under the placeholder so they can still be
    // renamed or deleted after their last chat leaves.
    const emptySections = hiding
      ? []
      : space.botSections.map((section) => ({
          key: showSpaceNames ? `space:${space.id}:section:${section.id}` : `section:${section.id}`,
          title: showSpaceNames ? space.name : section.name,
          subtitle: showSpaceNames ? section.name : undefined,
          bots: [] as (typeof sections)[number]["bots"],
          sectionId: section.id as string | null,
          showLock: showSpaceNames,
          emptySpaceId: undefined as string | undefined,
          spaceId: space.id,
          spaceName: space.name,
          ...spaceHeaderMenu(space, false),
        }));
    // A lone space is the selected one, so its settings stay in the account menu too.
    const lone = !showSpaceNames && !space.canRename && !space.canDelete;
    if (lone && (hiding || emptySections.length > 0)) {
      return emptySections;
    }
    return [
      {
        key: showSpaceNames ? `space:${space.id}:empty` : `space:${space.id}:actions`,
        title: space.name,
        subtitle: undefined,
        bots: [],
        sectionId: null,
        showLock: showSpaceNames,
        emptySpaceId: showSpaceNames ? space.id : undefined,
        spaceId: space.id,
        spaceName: space.name,
        ...spaceHeaderMenu(space, true),
      },
      ...emptySections,
    ];
  });
}

/** Count the unread flags the roster renders. Pass unfiltered groups for the app badge. */
export function countUnreadChats(
  sections: readonly { bots: readonly { chat: { unread?: boolean } }[] }[],
): number {
  return sections.reduce(
    (count, section) => count + section.bots.filter((item) => item.chat.unread).length,
    0,
  );
}
